import { startTransition, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import useQueryString from "utils/customHooks/useQueryString";
import {
  useTrendsPageFilterState,
  useVisibleProcesses,
  useEffectOnce,
  useTrendChartBinSize,
  useFocusRange,
  useUpdateBinSizeToBiggestAllowed,
  getAllowedBinSizes,
} from "utils/customHooks/genericHooks";
import { isEqual, cloneDeep } from "lodash";
import { filterObjectByRemovingKeys } from "utils/arrayUtils";
import {
  initialTrendsPageFilterState,
  updateTrendsPageFilterState,
  setFocusRange,
  updateTrendChartBinSize,
  resetTrendsPageState,
  TRENDS_PAGE_FINISH_INITIAL_FILTER,
} from "providers/GlobalStateProvider";
import queryString from "query-string";
import { useDispatch } from "react-redux";
import { useSelector } from "store/useSelector";
import { TrendChartBinSizesEnum } from "types/enums";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useMount } from "constants/constants";
import { ONE_DAY_OF_MS, getDayStart } from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { isVariationOfAll } from "components/FlightTrends/TrendsFilters/filtersDrawerUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { TrendsPageStartRangesEnum } from "components/DataGetter/FlightConfig/FlightConfigTypes/GeneralConfigType";
import { startOfWeek as getStartOfWeek } from "date-fns";

/** when the trendsPageFilterState or other variables (binSize) change, add them as query params to the url */
// tslint:disable-next-line: no-big-function
const SyncTrendsPageFilterStateToUrl = () => {
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const { trendChartBinSize } = useTrendChartBinSize();
  const parsedQuery = useQueryString();
  const focusRange = useFocusRange();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dayStartMsOffset } = useSettings();
  const { pathname, search } = useLocation();
  const [
    {
      general: { trendsPageOverrideGe },
    },
  ] = useFlightConfig();
  const isTrendsPageFinishedInitialFilter = useSelector(
    state => state.legacy.isTrendsPageFinishedInitialFilter
  );
  const visibleProcesses = useVisibleProcesses();
  const updateBinSizeToBiggestAllowed = useUpdateBinSizeToBiggestAllowed();

  const isProcessInQuery =
    "process" in parsedQuery && typeof parsedQuery.process === "string";
  const processFromQuery = visibleProcesses.find(p =>
    [p.name, p.id].includes(String(parsedQuery.process))
  );

  // reset to default trends page state if query is empty
  useMount(() => {
    const isQueryEmpty = Object.keys(parsedQuery).length === 0;
    if (isQueryEmpty) {
      dispatch(resetTrendsPageState());
    }
  });

  // sync filter state to url (when we change something, update the url)
  useEffect(() => {
    if (!isTrendsPageFinishedInitialFilter) {
      return;
    }
    let newQuery = { ...parsedQuery };
    const currentPath = `${pathname}${search}`;

    // for each trends page filter state entry,
    // compare it to the initial state; if it's different, add it to the url
    Object.entries(trendsPageFilterState).forEach(([key, val]) => {
      // if it's workScheduleId, use key "workSchedule" in the query instead
      const queryKey = key === "workScheduleId" ? "workSchedule" : key;
      const initialFilterState = initialTrendsPageFilterState[key];

      // is it the default (same as it appears on first load)?
      const isDefaultValue =
        typeof val === "object" // if comparing Process (the only "object" type)
          ? // compare everything but its ckey (it doesn't get populated right away)
            isEqual(
              filterObjectByRemovingKeys(val as any, ["ckey"]),
              filterObjectByRemovingKeys(initialFilterState, ["ckey"])
            )
          : val === initialFilterState;

      newQuery = isDefaultValue
        ? // if the filter item is its default value, remove it from the url
          (filterObjectByRemovingKeys(newQuery, [queryKey]) as typeof newQuery)
        : // otherwise add it to the url
          {
            ...newQuery,
            [queryKey]:
              typeof val === "object" && "id" in val
                ? // if it's a process, use its process.id
                  val.id
                : val,
          };
    });

    // when we change the bin size, sync it to the url params
    const isBinSizeSynced = parsedQuery.binSize === String(trendChartBinSize);
    if (!isBinSizeSynced) {
      // always add binSize to the url
      // (in case we want to change the defaults later, this won't invalidate links)
      newQuery = { ...newQuery, binSize: String(trendChartBinSize) };
    }

    // when we change the shift name, sync it to the url params
    const isShiftNameSynced =
      !parsedQuery.shiftName || // ok to not show shiftName in url
      parsedQuery.shiftName === String(trendsPageFilterState.shiftName);
    if (!isShiftNameSynced) {
      newQuery = {
        ...newQuery,
        shiftName: String(trendsPageFilterState.shiftName),
      };
    }

    // when we change the process, sync it to the url params
    const isProcessSynced =
      !parsedQuery.process || // ok to not show process in url
      parsedQuery.process === String(trendsPageFilterState.process.name);
    if (!isProcessSynced && processFromQuery) {
      newQuery = {
        ...newQuery,
        process: String(trendsPageFilterState.process.id),
      };
    }

    // set url params ge,lt when we change the focus range
    const isFocusRangeSynced =
      !focusRange ||
      (parsedQuery.ge === String(focusRange.ge) &&
        parsedQuery.lt === String(focusRange.lt));
    if (!isFocusRangeSynced && focusRange) {
      newQuery = {
        ...newQuery,
        ge: String(focusRange.ge),
        lt: String(focusRange.lt),
      };
    }

    // finally, update the url if necessary
    const newQueryString = queryString.stringify(newQuery);

    const newPath = `${pathname}${newQueryString ? "?" + newQueryString : ""}`;
    if (newPath !== currentPath) {
      // navigate to the new url
      navigate(newPath, { replace: true }); // ? we could also navigate(newPath) here if we want to add a new entry to the browser history (breaks browser back button)
    }
  }, [
    trendsPageFilterState,
    navigate,
    pathname,
    search,
    trendChartBinSize,
    parsedQuery,
    focusRange,
    dayStartMsOffset,
    isTrendsPageFinishedInitialFilter,
    processFromQuery,
  ]);

  // first we have to make sure the work schedule from the url (or default of "1") is put into the store
  useMount(() => {
    const workScheduleIdFromUrl = String(parsedQuery.workSchedule ?? 1);
    dispatch(
      updateTrendsPageFilterState({
        newFilterState: {
          ...trendsPageFilterState,
          workScheduleId: workScheduleIdFromUrl,
        },
      })
    );
  });

  // sync url to filter state on mount only (when we load the page, apply the url to the state)
  const shouldRun = Boolean(
    visibleProcesses.length > 0 &&
      (!isProcessInQuery ||
        processFromQuery ||
        isVariationOfAll(String(parsedQuery.process))) // if process is in the query, wait until we have it
  );
  const timeZoneMsOffset = useTimeZoneMsOffset();

  useEffectOnce({
    callback: () => {
      const isFilteredByUrl = Object.keys(parsedQuery).length > 0;
      const newFilterState = cloneDeep(trendsPageFilterState);
      if (isFilteredByUrl) {
        Object.entries(parsedQuery).forEach(([key, val]) => {
          // if it's something we can filter by (e.g. "shiftName" but not "ge"),
          // apply the filter
          if (key in trendsPageFilterState) {
            // this applies to:
            // - group: string;
            // - process: Process;
            // - shiftName: string;
            // - tag: string;
            // - pnlCategory: string;

            // if it's a process, find the rest of it
            if (key === "process") {
              newFilterState[key] =
                visibleProcesses.find(({ id }) => id === val) ||
                newFilterState[key];
            } else {
              newFilterState[key] = val;
            }
          }
        });
      }

      // update if necessary (if it's not equal)
      if (!isEqual(newFilterState, trendsPageFilterState)) {
        dispatch(updateTrendsPageFilterState({ newFilterState }));
      }

      // if ge & lt are in the query, we'll set the focus range
      let newFocusRange = {
        ge: 0,
        lt: 0,
      };
      if ("ge" in parsedQuery && "lt" in parsedQuery) {
        newFocusRange = {
          ge: Number(parsedQuery.ge),
          lt: Number(parsedQuery.lt),
        };
      }
      // if ge but no lt, go to ge + 1 day
      else if ("ge" in parsedQuery && !("lt" in parsedQuery)) {
        newFocusRange = {
          ge: Number(parsedQuery.ge),
          lt: Number(parsedQuery.ge) + ONE_DAY_OF_MS,
        };
      }
      // if there's no ge/lt in the query, go to this week
      else if (!("ge" in parsedQuery) && !("lt" in parsedQuery)) {
        const dayOfWeek = new Date().getDay();
        const startOfWeek =
          getStartOfWeek(Date.now() - 1 * ONE_DAY_OF_MS).getTime() +
          dayStartMsOffset +
          timeZoneMsOffset;
        // * if we are still in the first day of the Week, go to ge = last Week start, so we can still remain in trendChartBinSize=days (otherwise we could only show hour-bins)
        const isStillFirstDayOfWeek = dayOfWeek === 1;

        const geOfLastWeek = getDayStart(
          startOfWeek,
          dayStartMsOffset,
          timeZoneMsOffset
        );
        let ge = isStillFirstDayOfWeek ? geOfLastWeek : startOfWeek;
        if (trendsPageOverrideGe === TrendsPageStartRangesEnum.oneDay) {
          ge = getDayStart(
            Date.now() - 1 * ONE_DAY_OF_MS,
            dayStartMsOffset,
            timeZoneMsOffset
          );
        }
        newFocusRange = {
          ge,
          lt:
            Date.now() -
            1000 /* look at past data only to use optimized SegmentsGetterStaticPastDataOnly */,
        };
      }
      // if binSize in the query, set it
      const isBinSizeInQuery =
        "binSize" in parsedQuery && typeof parsedQuery.binSize === "string";
      dispatch(setFocusRange(newFocusRange));

      if (isBinSizeInQuery) {
        const newBinSize = TrendChartBinSizesEnum[String(parsedQuery.binSize)];
        const dateRangeDuration = newFocusRange.lt - newFocusRange.ge;
        const allowedBinSizes = getAllowedBinSizes(dateRangeDuration);
        const isBinSizeAllowed = allowedBinSizes.includes(newBinSize);
        dispatch(
          updateTrendChartBinSize(
            isBinSizeAllowed
              ? newBinSize
              : allowedBinSizes[allowedBinSizes.length - 1]
          )
        );
      } else {
        updateBinSizeToBiggestAllowed(newFocusRange);
      }

      startTransition(() => {
        dispatch({
          type: TRENDS_PAGE_FINISH_INITIAL_FILTER,
        });
      });
    },
    shouldRun,
    dependencies: [focusRange, visibleProcesses],
  });

  return null;
};

export default SyncTrendsPageFilterStateToUrl;
